import { createFileRoute } from "@tanstack/react-router";
import { contactLink } from "@/lib/analytics";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import portrait from "@/assets/sylwia-portrait.jpg.asset.json";
import ogSocial from "@/assets/og-sylwia-sitkowska.jpg";
import { abs , breadcrumbSchema } from "@/content/seo";

const title = "Polityka prywatności | Sylwia Sitkowska";
const description =
  "Informacja o przetwarzaniu danych osobowych przekazanych przez formularz kontaktowy na stronie Sylwii Sitkowskiej.";
const ogImage = abs(ogSocial);

export const Route = createFileRoute("/polityka-prywatnosci")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sylwiasitkowska.pl/polityka-prywatnosci" },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: "https://sylwiasitkowska.pl/polityka-prywatnosci" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbSchema([
            { name: "Strona główna", path: "/" },
            { name: "Polityka prywatności", path: "/polityka-prywatnosci" },
          ]),
        ),
      },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="tresc-glowna">
        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <p className="eyebrow">Dane osobowe</p>
            <h1 className="display-xl mt-8 max-w-3xl text-[2.2rem] md:text-[3rem]">
              Polityka prywatności
            </h1>
          </div>
        </section>

        <section className="bg-surface">
          <div className="container-page max-w-3xl section-pad">
            <div className="space-y-10 text-base leading-relaxed text-ink/80">
              <div>
                <h2 className="display-xl text-[1.4rem] text-ink">Kto przetwarza dane</h2>
                <p className="mt-4">
                  Administratorem danych przekazanych przez formularz kontaktowy i formularz
                  rezerwacji jest Sylwia Sitkowska, prowadząca działalność pod nazwą PsychoLogika,
                  NIP 1131986700. Kontakt w
                  sprawie danych:{" "}
                  <a
                    href="mailto:kontakt@sylwiasitkowska.pl"
                    {...contactLink("polityka_prywatnosci")}
                    className="border-b border-coral text-ink hover:text-coral-text"
                  >
                    kontakt@sylwiasitkowska.pl
                  </a>
                  .
                </p>

              </div>
              <div>
                <h2 className="display-xl text-[1.4rem] text-ink">Jakie dane i w jakim celu</h2>
                <p className="mt-4">
                  <strong className="font-medium text-ink">Formularz kontaktowy:</strong> imię i
                  nazwisko, adres e-mail, temat i treść wiadomości. Dane służą wyłącznie do
                  udzielenia odpowiedzi i ewentualnego ustalenia warunków współpracy.
                </p>
                <p className="mt-4">
                  <strong className="font-medium text-ink">Formularz rezerwacji:</strong> imię i
                  nazwisko, adres e-mail, opcjonalnie numer telefonu, rola zawodowa, preferowany
                  termin oraz dane do faktury. Dane służą do potwierdzenia terminu spotkania,
                  rozliczenia i wystawienia faktury.
                </p>
              </div>
              <div>
                <h2 className="display-xl text-[1.4rem] text-ink">Komu powierzam dane</h2>
                <p className="mt-4">
                  Dane mogą być przetwarzane przez podmioty, z których usług korzystam przy
                  prowadzeniu strony i kontaktu: dostawcę hostingu strony, dostawcę bazy danych
                  formularzy oraz dostawcę poczty elektronicznej. Podmioty te przetwarzają dane
                  wyłącznie na moje polecenie i w zakresie niezbędnym do świadczenia usługi. Dane
                  nie są przekazywane do państw trzecich poza przypadkami wynikającymi z korzystania
                  z tych usług, zawsze na podstawie odpowiednich zabezpieczeń.
                </p>
              </div>

              <div>
                <h2 className="display-xl text-[1.4rem] text-ink">Podstawa i czas przetwarzania</h2>
                <p className="mt-4">
                  Podstawą jest mój prawnie uzasadniony interes polegający na prowadzeniu
                  korespondencji oraz podjęcie działań przed zawarciem umowy. Dane przechowuję tak
                  długo, jak jest to potrzebne do obsługi sprawy, a następnie przez okres wynikający
                  z przepisów prawa.
                </p>
              </div>
              <div>
                <h2 className="display-xl text-[1.4rem] text-ink">Twoje prawa</h2>
                <p className="mt-4">
                  Masz prawo dostępu do swoich danych, ich sprostowania, usunięcia, ograniczenia
                  przetwarzania, wniesienia sprzeciwu oraz wniesienia skargi do Prezesa Urzędu
                  Ochrony Danych Osobowych. Podanie danych jest dobrowolne, ale niezbędne do
                  udzielenia odpowiedzi.
                </p>
              </div>
              <div>
                <h2 className="display-xl text-[1.4rem] text-ink">Poufność</h2>
                <p className="mt-4">
                  Treść wiadomości traktuję poufnie. Danych nie sprzedaję i nie wykorzystuję do
                  celów marketingowych bez Twojej zgody.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
